import React, { useState, useEffect } from 'react';

const VolunteerForm = ({ volunteer = null, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    location: '',
    email: '',
    password: '',
    status: 'Active'
  });

  useEffect(() => {
    if (volunteer) {
      setFormData({
        name: volunteer.name || '',
        phone: volunteer.phone || '',
        address: volunteer.address || '',
        location: volunteer.location || '',
        email: volunteer.email || '',
        password: '',
        status: volunteer.status || 'Active'
      });
    }
  }, [volunteer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone || !formData.password) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Please enter a valid email address');
      return;
    }

    // Validate phone number (basic validation)
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }

    // Validate password (minimum 8 characters)
    if (formData.password.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }

    try {
      // Disable the submit button
      const submitButton = e.target.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Saving...';
      }

      // Submit the form
      const result = await onSubmit(formData);
      console.log('Form submission result:', result);

      // If we get here, the submission was successful
      if (result && result.user_credentials) {
        // Show credentials in a modal or alert
        alert(`Volunteer created successfully!\n\nLogin credentials:\nEmail: ${result.user_credentials.username}\nPassword: ${result.user_credentials.password}`);
      } else {
        alert('Volunteer created successfully!');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Show error message
      let errorMessage = 'Failed to create volunteer. ';
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.detail) {
          errorMessage += errorData.detail;
        } else if (errorData.email) {
          errorMessage += `Email error: ${errorData.email}`;
        } else if (errorData.password) {
          errorMessage += `Password error: ${errorData.password}`;
        } else if (typeof errorData === 'object') {
          const errorMessages = Object.entries(errorData)
            .map(([field, value]) => `${field}: ${value}`)
            .join(', ');
          errorMessage += errorMessages;
        }
      } else if (error.message) {
        errorMessage += error.message;
      }
      
      alert(errorMessage);
    } finally {
      // Re-enable the submit button
      const submitButton = e.target.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = volunteer ? 'Update Volunteer' : 'Add Volunteer';
      }
    }
  };

  return (
    <div style={styles.formContainer}>
      <h2 style={styles.title}>
        {volunteer ? 'Edit Volunteer' : 'Add New Volunteer'}
      </h2>

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label htmlFor="name" style={styles.label}>Volunteer Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="phone" style={styles.label}>Mobile</label>
            <input
              type="text"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>
        </div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label htmlFor="address" style={styles.label}>Village</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="location" style={styles.label}>District</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>
        </div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label htmlFor="email" style={styles.label}>Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="password" style={styles.label}>Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>
        </div>

        <div style={styles.formGroup}>
          <label htmlFor="status" style={styles.label}>Status</label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            style={styles.input}
            required
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Pending">Pending</option>
          </select>
        </div>

        <div style={styles.buttonGroup}>
          <button type="button" onClick={onCancel} style={styles.cancelButton}>
            Cancel
          </button>
          <button type="submit" style={styles.submitButton}>
            {volunteer ? 'Update' : 'Add'} Volunteer
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  formContainer: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    maxWidth: '800px',
    margin: '0 auto',
  },
  title: {
    marginTop: 0,
    marginBottom: '1.5rem',
    color: '#2c3e50',
    textAlign: 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#3498db',
    marginTop: '1.5rem',
    marginBottom: '1rem',
    borderBottom: '1px solid #eee',
    paddingBottom: '0.5rem',
  },
  formRow: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1rem',
  },
  formGroup: {
    flex: 1,
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '4px',
    border: '1px solid #ddd',
    fontSize: '1rem',
  },
  inputWithUnit: {
    display: 'flex',
    alignItems: 'center',
  },
  inputUnit: {
    flex: 1,
    padding: '0.75rem',
    borderRadius: '4px 0 0 4px',
    border: '1px solid #ddd',
    borderRight: 'none',
    fontSize: '1rem',
  },
  unitSelect: {
    width: '80px',
    padding: '0.75rem',
    borderRadius: '0 4px 4px 0',
    border: '1px solid #ddd',
    fontSize: '1rem',
    backgroundColor: '#f8f9fa',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  checkbox: {
    marginRight: '0.5rem',
  },
  checkboxLabel: {
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  fileUpload: {
    border: '2px dashed #ddd',
    padding: '1rem',
    borderRadius: '4px',
    textAlign: 'center',
    backgroundColor: '#f8f9fa',
  },
  fileInput: {
    width: '100%',
    cursor: 'pointer',
  },
  fileUploadNote: {
    marginTop: '0.5rem',
    color: '#7f8c8d',
    fontSize: '0.9rem',
    fontStyle: 'italic',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '2rem',
  },
  cancelButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  submitButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
};

export default VolunteerForm;
